import { Check, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatDate } from '../../format-date';
import { decideVatProposal, type Supplier, type VatProposal, type WriteOutcome } from './api';
import { Notice } from './Notice';

/**
 * «Partita IVA da verificare» (board 3.2): a VAT number nexpense or a receipt proposed. Gestione
 * and the approvers confirm it (then VIES checks it) or reject it; the decision stays in the
 * history. The others see the mark only: the API gives them no proposals.
 */
export function VatProposalNotice({
  supplier,
  onWrite,
}: {
  readonly supplier: Supplier;
  readonly onWrite: (outcome: WriteOutcome, done: string) => void;
}) {
  const pending = supplier.can.decideVatProposals
    ? supplier.vatProposals.filter((proposal) => proposal.state === 'pending')
    : [];
  return (
    <>
      {pending.map((proposal) => (
        <Proposal
          key={proposal.id}
          supplierId={supplier.id}
          proposal={proposal}
          onWrite={onWrite}
        />
      ))}
    </>
  );
}

function Proposal({
  supplierId,
  proposal,
  onWrite,
}: {
  readonly supplierId: string;
  readonly proposal: VatProposal;
  readonly onWrite: (outcome: WriteOutcome, done: string) => void;
}) {
  const { t } = useTranslation('suppliers');
  const [busy, setBusy] = useState<'confirm' | 'reject' | null>(null);
  const vat = `${proposal.vatCountry} ${proposal.vat}`;

  async function decide(decision: 'confirm' | 'reject') {
    if (busy !== null) return;
    setBusy(decision);
    const outcome = await decideVatProposal(supplierId, proposal.id, decision);
    setBusy(null);
    onWrite(outcome, t(decision === 'confirm' ? 'verify.confirmed' : 'verify.rejected', { vat }));
  }

  return (
    <Notice
      tone="warning"
      icon={Sparkles}
      title={t('verify.title', { vat })}
      actions={
        <>
          <Button size="sm" disabled={busy !== null} onClick={() => void decide('confirm')}>
            <Check aria-hidden="true" />
            {t('verify.confirm')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => void decide('reject')}
          >
            {t('verify.reject')}
          </Button>
        </>
      }
    >
      <p>
        {t(`verify.source.${proposal.source}`, { date: formatDate(proposal.createdAt) })}{' '}
        {t('verify.ask')}
      </p>
      {proposal.viesName ? (
        <p className="text-muted-foreground">
          {t('verify.registerSays', {
            name: proposal.viesName,
            address: proposal.viesAddress ?? '',
          })}
        </p>
      ) : null}
    </Notice>
  );
}
